// src/components/SegmentedInput.jsx — the fill-in-the-segments field, ours rather than the
// browser's.
//
// Owner, 2026-09-02, pointing at the attendance modal's time fields: "I liked this fill in date
// style here can we use this in every other form? candidate modal to agent modal to attendance
// modals" … "meeting modal too" … then, immediately: "wait no PM AM / 24 hour system".
//
// Those two asks pull in opposite directions and the second one decides it. The style they liked
// was the NATIVE `<input type="time">` — segments you type into, `--:-- --` when empty. But that
// control renders 12-hour with an AM/PM segment on their browser, and there is no way to change
// it: MEASURED 2026-09-02 in the owner's own Chrome, `lang="en-GB"`, `lang="ka"` and `lang="de"`
// inputs all still drew `--:-- --`. Chrome formats date and time inputs from the browser's UI
// locale and ignores the element's `lang` (Firefox honours it; Chrome does not). There is no
// attribute, no CSS and no React prop that changes this — the only lever is the agent's own
// browser language setting, on every agent's machine.
//
// So the style is reproduced instead of borrowed: the same segmented filling, the same
// placeholder per segment, the same type-and-it-advances behaviour — and 24-hour always, on
// every machine, whatever locale the browser is in.
//
// ⚠️ AND THE SAME ARGUMENT APPLIES TO DATES, which is why they moved too. A native date input on
// an en-US browser renders mm/dd/yyyy, so `03/09` reads as March 9 to the control and 3 September
// to the office. That is not a style preference; it is two readings of one number in a company
// that books viewings by date. Day-first, always.
//
// ⚠️ ONE INPUT PER SEGMENT, not a masked single field. A mask has to own the caret — where it
// sits after a keystroke, what a paste means, what Backspace deletes mid-string — and every one
// of those is a place to be subtly wrong. Real inputs give focus, selection and keyboard
// behaviour for free; the flex box around them is what makes three of them look like one field.
import { useEffect, useRef, useState } from 'react'
// The deciding half lives in lib/ and is tested there: what composes to a value, what composes
// to '' ("no date yet"), and the round trip that stops a stored value clearing its own boxes.
import { dateCompose, dateExplode, timeCompose, timeExplode, completeIfOnlyOption } from '../lib/segmentedValue.js'

const pad2 = n => String(n).padStart(2, '0')

const digits = s => String(s ?? '').replace(/\D/g, '')


const BOX = 'flex items-center gap-0.5 rounded-lg border border-input bg-background px-3 py-2 text-sm ' +
            'focus-within:outline-none focus-within:ring-2 focus-within:ring-ring'

/**
 * The shared machinery. `segs` describes the boxes left to right; `compose` turns segment text
 * into the stored string (or '' when it does not yet make a real value); `explode` turns a
 * stored string back into segment text.
 */
function Segmented({ segs, sep, value, onChange, compose, explode, className, disabled, ariaLabel }) {
  const [parts, setParts] = useState(() => explode(value))
  const refs = useRef([])
  // 🔴 THE LATEST SEGMENTS, READABLE SYNCHRONOUSLY. `parts` is state, so a handler that runs
  // before React re-renders sees the PREVIOUS value — and one does: advancing focus fires the
  // outgoing segment's blur SYNCHRONOUSLY, inside the same event as the change that filled it.
  // Owner-reported 2026-09-03: "I was not able to type in 11 it becomes 01". Reproduced exactly
  // — type 1, PAUSE, type 1: the DOM holds "11" and blur then pads the stale "1" to "01" over it.
  // ⚠️ It only happens when the digits are separated by a pause. Typed fast, both arrive in ONE
  // input event, blur reads an EMPTY stale value and returns early — so the bug is invisible to
  // synthetic typing and to anyone testing quickly, which is why it shipped.
  const partsRef = useRef(parts)

  // ⚠️ Re-seed from the PARENT, but only when the parent's value differs from what these boxes
  // already say. Seeding unconditionally would fight the person typing: every keystroke that has
  // not yet completed a value emits '', which would come straight back as "clear the boxes".
  useEffect(() => {
    if (compose(parts) !== (value || '')) { const next = explode(value); partsRef.current = next; setParts(next) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  const commit = next => {
    partsRef.current = next      // before setParts: a synchronous blur must see what was typed
    setParts(next)
    onChange(compose(next))
  }

  // ⚠️ OVERFLOW FLOWS FORWARD, and this is not a nicety — it was a real dropped-digit bug,
  // seen by typing "1330" into an empty time field: "13" landed, focus moved to the minutes, and
  // the "30" was GONE. The advance is a React state update, so a fast typist's next keystrokes
  // still arrive at the segment they are leaving; that segment was slicing them off and throwing
  // them away. Anyone typing at speed silently lost half of every time they entered.
  // Forwarding is also what the native control does, so it is the right behaviour twice over.
  const onSeg = (i, raw) => {
    const all = digits(raw)
    // Same reason as onBlur: two input events can land before React re-renders (typing across
    // segments at speed), and slicing STATE would rebuild the row from a stale copy — quietly
    // undoing the segment the previous keystroke just filled.
    const next = partsRef.current.slice()
    let at = i
    let rest = all
    while (at < segs.length) {
      next[at] = rest.slice(0, segs[at].len)
      rest = rest.slice(segs[at].len)
      // A lone digit that can start no valid value IS the value: "9" in an hour is 09, and the
      // caret moves on — the native control's behaviour. Without this, 9 → 4 → 5 typed as
      // 09:45 accumulated "94", blur clamped it to 23, and the 5 landed in the minutes: 23:05.
      // Only for two-digit segments; a year has no such shortcut.
      if (segs[at].len === 2) {
        const done = completeIfOnlyOption(next[at], segs[at].max)
        if (done) next[at] = done
      }
      if (!rest) break
      at += 1
    }
    commit(next)
    // Land on the last segment the digits reached; move past it once it is full, the way typing
    // 0 then 9 into a 2-digit month jumps to the year without touching Tab.
    const full = (next[at] || '').length === segs[at]?.len
    const target = full && at < segs.length - 1 ? at + 1 : at
    if (target !== i) refs.current[target]?.focus()
  }

  const onKey = (i, e) => {
    const seg = segs[i]
    const cur = partsRef.current[i] || ''
    if (e.key === 'Backspace' && !cur && i > 0) { e.preventDefault(); refs.current[i - 1]?.focus() }
    if (e.key === 'ArrowLeft' && i > 0 && e.target.selectionStart === 0) {
      e.preventDefault(); refs.current[i - 1]?.focus()
    }
    if (e.key === 'ArrowRight' && i < segs.length - 1 && e.target.selectionStart === cur.length) {
      e.preventDefault(); refs.current[i + 1]?.focus()
    }
    // Up/Down step the segment, like the native control. Wrapping is deliberate: after 23 the
    // next hour is 00, and refusing to move would read as a stuck key.
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault()
      const up = e.key === 'ArrowUp'
      const span = seg.max - seg.min + 1
      const from = cur === '' ? (up ? seg.min - 1 : seg.min + 1) : Number(cur)
      const stepped = seg.min + (((from - seg.min + (up ? 1 : -1)) % span) + span) % span
      const next = partsRef.current.slice()
      next[i] = seg.len === 4 ? String(stepped).padStart(4, '0') : pad2(stepped)
      commit(next)
    }
  }

  // ⚠️ PAD ON BLUR, never while typing. Typing "9" into the month must stay "9" until the person
  // leaves — padding it to "09" mid-keystroke moves the caret and eats the next digit.
  const onBlur = i => {
    const seg = segs[i]
    // ⚠️ partsRef, NOT `parts`. This handler routinely runs in the same event as the change
    // that filled the segment — focus moves, the outgoing input blurs, and `parts` is still one
    // render behind. Reading state here padded a stale "1" to "01" ON TOP of the "11" the person
    // had actually typed. Anything in this component that runs during an event must read the ref.
    const live = partsRef.current
    const v = live[i]
    if (!v) return
    let n = Number(v)
    if (n < seg.min) n = seg.min
    if (n > seg.max) n = seg.max
    const next = live.slice()
    next[i] = seg.len === 4 ? String(n).padStart(4, '0') : pad2(n)
    if (next[i] !== v) commit(next)
  }

  return (
    <div className={className || BOX} aria-label={ariaLabel}>
      {segs.map((seg, i) => (
        <span key={seg.key} className="contents">
          {i > 0 && <span className="text-muted-foreground/50 select-none px-0.5">{sep}</span>}
          <input
            ref={el => { refs.current[i] = el }}
            type="text"
            inputMode="numeric"
            disabled={disabled}
            aria-label={seg.key}
            value={parts[i]}
            placeholder={seg.ph}
            onChange={e => onSeg(i, e.target.value)}
            onKeyDown={e => onKey(i, e)}
            onBlur={() => onBlur(i)}
            onFocus={e => e.target.select()}
            // Width in `ch` so a 4-digit year is visibly wider than a 2-digit month, the way the
            // native control lays them out.
            style={{ width: `${seg.len + 0.5}ch` }}
            className="bg-transparent text-center tabular-nums outline-none placeholder:text-muted-foreground/40 disabled:opacity-50"
          />
        </span>
      ))}
    </div>
  )
}

// ── The date field: dd / mm / yyyy in, ISO YYYY-MM-DD out ───────────────────────────────────
const DATE_SEGS = [
  { key: 'day',   len: 2, min: 1,    max: 31,   ph: 'dd' },
  { key: 'month', len: 2, min: 1,    max: 12,   ph: 'mm' },
  { key: 'year',  len: 4, min: 1900, max: 2999, ph: 'yyyy' },
]


// `placeholder` is accepted and ignored — each segment carries its own hint. Kept so the eleven
// existing call sites need no edit.
// ⚠️ `min` / `max` are REAL CONSTRAINTS, not decoration, and dropping them was the one way
// this change could have done damage. The native input enforced them; two call sites depend on
// it — the client modal's check-out cannot precede the move-in, and the listings day picker
// refuses a day that has not happened. So the composer refuses an out-of-range date the same way
// it refuses 31 February: by emitting '', which every caller already treats as "no date yet".
export function DateInput({ value, onChange, className = '', placeholder, disabled, min, max }) {
  void placeholder
  const compose = parts => {
    const iso = dateCompose(parts)
    if (!iso) return ''
    if (min && iso < min) return ''
    if (max && iso > max) return ''
    return iso
  }
  return (
    <Segmented segs={DATE_SEGS} sep="/" value={value} onChange={onChange}
      compose={compose} explode={dateExplode}
      className={className} disabled={disabled} ariaLabel="date" />
  )
}

// ── The time field: HH : mm, twenty-four hours, everywhere ──────────────────────────────────
const TIME_SEGS = [
  { key: 'hour',   len: 2, min: 0, max: 23, ph: '--' },
  { key: 'minute', len: 2, min: 0, max: 59, ph: '--' },
]


// `placeholder` accepted and ignored, as on DateInput — the old TimeInput took one.
export function TimeInput({ value, onChange, className = '', placeholder, disabled }) {
  void placeholder
  return (
    <Segmented segs={TIME_SEGS} sep=":" value={value} onChange={onChange}
      compose={timeCompose} explode={timeExplode}
      className={className} disabled={disabled} ariaLabel="time" />
  )
}
